import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const SECRET_KEY = 'hgdcr';

function xorCipher(bytes) {
  const keyBytes = new TextEncoder().encode(SECRET_KEY);
  return bytes.map((byte, idx) => byte ^ keyBytes[idx % keyBytes.length]);
}

function encode(input) {
  const inputBytes = new TextEncoder().encode(input);
  const encodedBytes = xorCipher(inputBytes);
  return btoa(String.fromCharCode(...encodedBytes));
}

function decode(encodedInput) {
  const byteString = atob(encodedInput);
  const decodedBytes = Array.from(byteString, (char) => char.charCodeAt(0));
  const originalBytes = xorCipher(decodedBytes);
  return new TextDecoder().decode(new Uint8Array(originalBytes));
}

const createMutex = () => {
  let lock = Promise.resolve();

  return function queue(fn) {
    const run = () => fn().catch(console.error);
    const current = lock;
    lock = lock.then(() => run());
    return current;
  };
};

// Shared mutex across hook instances
const updateMutex = createMutex();

const useEncryptedURLState = (key, defaultValue) => {
  const history = useHistory();
  const location = useLocation();

  const getInitialState = () => {
    const searchParams = new URLSearchParams(location.search);
    const raw = searchParams.get(key);

    if (!raw) return defaultValue;

    try {
      const decrypted = decode(raw);
      return JSON.parse(decrypted);
    } catch (e) {
      console.warn(`Failed to decode or parse key "${key}" from URL`, e);
      return defaultValue;
    }
  };

  const [state, setState] = useState(getInitialState);

  const updateState = (newValue) => {
    updateMutex(async () => {
      const resolvedValue = typeof newValue === 'function' ? newValue(state) : newValue;
      setState(resolvedValue);

      const currentParams = new URLSearchParams(history.location.search);
      const encrypted = encode(JSON.stringify(resolvedValue));
      currentParams.set(key, encrypted);

      history.replace({
        pathname: location.pathname,
        search: currentParams.toString(),
      });
    });
  };

  return [state, updateState];
};

export default useEncryptedURLState;
